import React, { useEffect, useMemo } from "react";
import { DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { renderAvatar, ensureAvatarCss } from "@/lib/avatar2d";
import { renderMinotaur, MINOTAURS } from "@/lib/minotaur";
import { CHARACTERS } from "@/lib/avatarConfig";
import { queenCard } from "@/lib/lhQueen";
import { pieceSvg } from "@/lib/chessPieces";

// "Games" section of the header menu (Jack, 2026-10-20): a picture tile per
// extra game. Raider Races is open to everyone (Jack, 2026-10-23);
// Minotauro stays testers only (user.maze_test), Squads is Jack's only
// for now (user.squads_test, 2026-10-23), and so is Squads 2.0
// (user.squads2_test, 2026-10-28). Vowel Prowl is testers only
// (user.maze_test, 2026-10-26), and so are Arena, Stream and
// Minimal (2026-11-03, trial game-screen looks). The Campaign is Jack's only for now
// (user.campaign_test, 2026-10-29), and so is Raidaverse
// (user.raidaverse_test, 2026-10-29). Det test is admin only
// (user.det_test, 2026-10-30), and so is starting 4-player Fortress
// games (/four, 2026-10-30; invited players reach it from Your games).
const ART_CLS = "absolute h-auto [&_svg]:w-full [&_svg]:h-auto";

function Tile({ name, sub, bg, art, onSelect, testId, disabled }) {
  return (
    <DropdownMenuItem
      onSelect={(e) => { if (disabled) e.preventDefault(); else onSelect(); }}
      aria-disabled={disabled || undefined}
      className={`flex-col items-stretch p-1.5 pb-2 rounded-lg border border-border bg-muted/40 text-center ${disabled ? "cursor-default" : ""}`}
      data-testid={testId}
    >
      <div className="relative h-16 rounded-md overflow-hidden" style={{ background: bg }}>{art}</div>
      <div className="text-[12.5px] font-bold mt-1 leading-tight">{name}</div>
      {sub && <div className="text-[10px] text-amber-500 leading-tight">{sub}</div>}
    </DropdownMenuItem>
  );
}

export default function GamesMenu({ navigate, maze = false, squads = false, squads2 = false, det = false, letterheist = false, campaign = false, raidaverse = false }) {
  useEffect(() => ensureAvatarCss(), []);
  const art = useMemo(() => {
    const m = MINOTAURS.bronze;
    const look = (id) => (CHARACTERS.find((c) => c.id === id) || CHARACTERS[0]).look;
    return {
      mino: renderMinotaur(m.look, m.palette, { pose: "taunt" }),
      a: renderAvatar(look("viking"), { pose: "walk" }),
      b: renderAvatar(look("valkyrie"), { pose: "walk" }),
      king: renderAvatar(look("king"), { pose: "idle" }),
      orc: renderAvatar(look("orc"), { pose: "idle" }),
      elf: renderAvatar(look("frost"), { pose: "idle" }),
      // Letterheist: its yellow and blue queens (lib/lhQueen.js).
      lhA: queenCard("y"),
      lhB: queenCard("b"),
    };
  }, []);
  return (
    <>
      <DropdownMenuSeparator />
      <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-muted-foreground py-1">Games</DropdownMenuLabel>
      <div className="grid grid-cols-2 gap-1.5 px-1 pb-1">
        {campaign && <Tile
          name="Campaign"
          sub="Test"
          bg="linear-gradient(#16301f, #18273d)"
          art={<>
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 64" aria-hidden="true">
              <polyline points="14,54 38,44 62,48 84,30 60,16" fill="none" stroke="#f393a1" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              {[[14, 54], [38, 44], [62, 48], [84, 30]].map(([x, y]) => <circle key={x} cx={x} cy={y} r="5" fill="#f393a1" />)}
              <circle cx="60" cy="16" r="6" fill="#141822" stroke="#f393a1" strokeWidth="2.5" />
            </svg>
          </>}
          onSelect={() => navigate("/campaign")}
          testId="menu-campaign"
        />}
        {raidaverse && <Tile
          name="Raidaverse"
          sub="Test"
          bg="linear-gradient(#18273d, #141822)"
          art={<svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 64" aria-hidden="true">
            {/* A tiny half board: steel walls, a river with a bridge, a POW square. */}
            {[18, 30, 42, 54].map((x) => <rect key={x} x={x} y="14" width="10" height="10" rx="1" fill="#9aa3ad" stroke="#5d6672" />)}
            <rect x="6" y="30" width="88" height="10" fill="#2f6f9e" />
            <rect x="66" y="30" width="10" height="10" fill="#7a5a3a" />
            <rect x="74" y="12" width="12" height="12" rx="1" fill="#98bf86" />
            <rect x="38" y="46" width="24" height="14" rx="1" fill="#3a2430" stroke="#f393a1" />
          </svg>}
          onSelect={() => navigate("/raidaverse")}
          testId="menu-raidaverse"
        />}
        {maze && <Tile
          name="Minotauro"
          bg="radial-gradient(circle at 50% 70%, #7b3fbf, #2a1640)"
          art={<div className={ART_CLS} style={{ width: 110, left: "50%", top: -4, transform: "translateX(-50%)" }} dangerouslySetInnerHTML={{ __html: art.mino }} />}
          onSelect={() => navigate("/maze")}
          testId="menu-maze"
        />}
        <Tile
          name="Raider Races"
          bg="linear-gradient(#3d6b3a, #2a4a28)"
          art={<>
            <div className="absolute inset-x-0 top-1.5 h-2" style={{ background: "repeating-conic-gradient(#fff 0 25%, #222 0 50%) 0 0 / 8px 8px" }} />
            <div className={ART_CLS} style={{ width: 54, left: 2, top: 14 }} dangerouslySetInnerHTML={{ __html: art.a }} />
            <div className={ART_CLS} style={{ width: 54, right: 2, top: 14 }} dangerouslySetInnerHTML={{ __html: art.b }} />
          </>}
          onSelect={() => navigate("/race")}
          testId="menu-raider-races"
        />
        {squads && <Tile
          name="Squads"
          bg="linear-gradient(#1d4a6b, #142a3d)"
          art={<>
            <div className={ART_CLS} style={{ width: 46, left: 0, top: 12 }} dangerouslySetInnerHTML={{ __html: art.orc }} />
            <div className={ART_CLS} style={{ width: 46, right: 0, top: 12 }} dangerouslySetInnerHTML={{ __html: art.elf }} />
            <div className={ART_CLS} style={{ width: 54, left: "50%", top: 4, transform: "translateX(-50%)" }} dangerouslySetInnerHTML={{ __html: art.king }} />
          </>}
          onSelect={() => navigate("/squads")}
          testId="menu-squads"
        />}
        {squads2 && <Tile
          name="Squads 2.0"
          bg="linear-gradient(#2b3346, #141822)"
          art={<div className="absolute inset-0 flex items-end justify-center gap-0.5 pb-1">
            <span className="block w-9 h-9" dangerouslySetInnerHTML={{ __html: pieceSvg("Q", "H") }} />
            <span className="block w-11 h-11" dangerouslySetInnerHTML={{ __html: pieceSvg("K", "A") }} />
            <span className="block w-9 h-9" dangerouslySetInnerHTML={{ __html: pieceSvg("N", "H") }} />
          </div>}
          onSelect={() => navigate("/squads2")}
          testId="menu-squads2"
        />}
        {det && <Tile
          name="Det test"
          sub="Test"
          bg="linear-gradient(#2b3346, #141822)"
          art={<div className="absolute inset-0 flex items-center justify-center gap-1">
            {[1, 3, 4, 2].map((n, i) => (
              <span key={i} className={`w-6 h-6 rounded-[4px] font-black text-sm flex items-center justify-center shadow ${i % 2 ? "bg-[#4FB0F0] text-[#0b2a40]" : "bg-[#f393a1] text-[#3a0f18]"}`}>{n}</span>
            ))}
          </div>}
          onSelect={() => navigate("/det")}
          testId="menu-det"
        />}
        {det && <Tile
          name="4-player Fortress"
          sub="Test"
          bg="linear-gradient(#2b3346, #141822)"
          art={<svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 64" aria-hidden="true">
            {/* The square board with a base in each corner. */}
            <rect x="22" y="4" width="56" height="56" fill="#302c2c" />
            <rect x="27" y="9" width="9" height="9" fill="#5fc77a" />
            <rect x="64" y="9" width="9" height="9" fill="#4FB0F0" />
            <rect x="27" y="46" width="9" height="9" fill="#f393a1" />
            <rect x="64" y="46" width="9" height="9" fill="#f2c94c" />
          </svg>}
          onSelect={() => navigate("/four")}
          testId="menu-four-fortress2"
        />}
        {maze && <Tile
          name="Vowel Prowl"
          bg="linear-gradient(#5b2a6e, #2e1638)"
          art={<div className="absolute inset-0 flex items-center justify-center gap-1">
            {["A", "E", "I"].map((l) => (
              <span key={l} className="w-6 h-6 rounded-[4px] bg-[#f393a1] text-[#3a0f18] font-black text-sm flex items-center justify-center shadow">{l}</span>
            ))}
          </div>}
          onSelect={() => navigate("/vowel-prowl")}
          testId="menu-vowel-prowl"
        />}
        {maze && <Tile
          name="Arena"
          sub="Test"
          bg="linear-gradient(#312e2b, #262421)"
          art={<div className="absolute inset-0 flex flex-col items-center justify-center gap-1">
            <div className="flex gap-0.5">
              {["T", "I", "D", "E"].map((l) => (
                <span key={l} className="w-5 h-5 rounded-[3px] bg-[#ffe28a] text-[#262421] font-black text-[11px] flex items-center justify-center" style={{ boxShadow: "inset 0 -2px 0 #e8735a" }}>{l}</span>
              ))}
            </div>
            <span className="px-2 h-3.5 rounded bg-[#81b64c] text-white text-[8px] font-black flex items-center">PLAY</span>
          </div>}
          onSelect={() => navigate("/arena")}
          testId="menu-arena"
        />}
        {maze && <Tile
          name="Stream"
          sub="Test"
          bg="linear-gradient(#141d3a, #0f1730)"
          art={<div className="absolute inset-0 flex items-end justify-center gap-0.5 pb-2">
            {["S", "T", "R", "E", "A", "M"].map((l) => (
              <span key={l} className="w-4 h-5 rounded-[4px] bg-white text-[#1b2545] font-black text-[10px] flex items-center justify-center" style={{ boxShadow: "inset 0 -2px 0 #c3c9d8" }}>{l}</span>
            ))}
          </div>}
          onSelect={() => navigate("/stream")}
          testId="menu-stream"
        />}
        {maze && <Tile
          name="Minimal"
          sub="Test"
          bg="linear-gradient(#26244a, #1c1b3a)"
          art={<div className="absolute inset-x-1.5 bottom-1 top-6 rounded-md bg-[#e9dcc6] flex items-center justify-center gap-0.5">
            {["M", "I", "N"].map((l) => (
              <span key={l} className="w-4 h-5 rounded-[3px] bg-[#fffaf0] text-[#3b2f22] font-black text-[10px] flex items-center justify-center" style={{ boxShadow: "inset 0 -2px 0 #d8c9ab" }}>{l}</span>
            ))}
            <span className="ml-1 px-1.5 h-3.5 rounded-full bg-[#c9577e] text-white text-[7px] font-black flex items-center">END</span>
          </div>}
          onSelect={() => navigate("/minimal")}
          testId="menu-minimal"
        />}
        {letterheist && <Tile
          name="Letterheist"
          sub="Board game"
          bg="radial-gradient(circle at 50% 120%, #3a5a86, #1b2a44)"
          art={<>
            <div className="absolute inset-x-1 bottom-0 top-9 rounded-sm bg-[#f7f4ec] border-2 border-[#8a5d34]" />
            <div className={ART_CLS} style={{ width: 40, left: 6, top: 16 }} dangerouslySetInnerHTML={{ __html: art.lhA }} />
            <div className={ART_CLS} style={{ width: 40, right: 6, top: 16 }} dangerouslySetInnerHTML={{ __html: art.lhB }} />
          </>}
          onSelect={() => navigate("/letterheist")}
          testId="menu-letterheist"
        />}
      </div>
    </>
  );
}
